<template>
  <div class="auth">
    <div class="auth-grid" :class="{'auth-grid--solo': !pitch}">
      <section v-if="pitch" class="pitch" aria-labelledby="pitch-title">
        <h1 id="pitch-title">Logiciel de facturation électronique français.</h1>
        <p class="lead">Gestion clients, prospection commerciale et comptabilité intégrées.</p>
        <ul class="checks">
          <li>Devis, acomptes et factures au format Factur-X</li>
          <li>Achats, stock et export comptable (FEC)</li>
          <li>Données hébergées en France</li>
        </ul>
        <p class="more">
          <a class="pub-link" :href="productUrl">Découvrir le Gestionnaire Citron</a> sur citrondigital.fr
        </p>
      </section>

      <div class="entry">
        <nav v-if="tab" class="tabs" aria-label="Connexion ou création de compte">
          <router-link to="/login" class="tab" :class="{'tab--active': tab === 'login'}" :aria-current="tab === 'login' ? 'page' : null">Connexion</router-link>
          <router-link to="/register" class="tab" :class="{'tab--active': tab === 'register'}" :aria-current="tab === 'register' ? 'page' : null">Créer mon compte</router-link>
        </nav>

        <div>
          <div class="sheet">
            <h2 v-if="title" class="sheet-title">{{ title }}</h2>
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {SITE_PRODUCT_URL} from "@/components/public/siteLinks";

  /**
   * Mise en page des pages d'accès (connexion, inscription, mot de passe, confirmation du mail, déconnexion).
   * À gauche, ce que fait le logiciel ; à droite, une « feuille » qui accueille le formulaire, avec deux onglets (connexion, inscription).
   * Props : tab ('login' | 'register' | '' pour aucun onglet), title (titre de la feuille), pitch (false : la feuille seule, centrée).
   */
  export default {
    name: 'AuthLayout',
    props: {
      tab: {type: String, default: ''},
      title: {type: String, default: ''},
      pitch: {type: Boolean, default: true},
    },
    data() {
      return {
        productUrl: SITE_PRODUCT_URL,
      }
    }
  }
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/public.styl';

// Papier réglé et, à gauche, la marge rouge du cahier
.auth
  position relative
  flex 1 0 auto
  display flex
  background-color PUB_PAPER

.auth-grid
  pub-container()
  position relative
  display grid
  gap 32px
  align-content center
  padding-top clamp(24px, 5vw, 64px)
  padding-bottom clamp(32px, 6vw, 80px)

// ---------------------------------------------------------- texte de gauche
.pitch
  h1
    margin 0
    color PUB_INK
    font-family PUB_FONT_DISPLAY
    font-weight 400
    font-size clamp(1.75rem, 6.5vw, 2.75rem) !important
    line-height 1.12
    text-align left
    letter-spacing -0.015em
    text-wrap balance
  .lead
    margin 14px 0 0
    max-width 34ch
    color PUB_INK_SOFT
    font-family PUB_FONT_TEXT
    font-size clamp(1.05rem, 2.4vw, 1.3rem)
    line-height 1.5

.checks, .more
  display none

// ---------------------------------------------------------- feuille et onglets
.entry
  width 100%
  max-width 460px
  margin 0 auto

.tabs
  display flex
  gap 22px
  margin 0 0 14px

.tab
  display inline-block
  padding 6px 2px
  border-bottom 3px solid transparent
  color PUB_INK
  font-family PUB_FONT_TEXT
  font-size 1rem
  font-weight 600
  text-decoration none
  &:hover
    border-bottom-color PUB_LEMON_DEEP
  &.tab--active
    border-bottom-color PUB_LEMON

// Même carte que sur le site : papier blanc, filet clair, liseré citron en haut
.sheet
  box-sizing border-box
  padding 26px 22px 28px
  background-color PUB_WHITE
  border 1px solid PUB_RULE
  border-top 4px solid PUB_LEMON
  border-radius 4px

.sheet-title
  margin 0 0 6px
  color PUB_INK
  font-family PUB_FONT_DISPLAY
  font-weight 400
  font-size 1.55rem !important
  line-height 1.2

// Le formulaire remplit la feuille (il avait sa propre largeur quand il était seul à l'écran)
.sheet
  :deep(.container-screen)
    flex none
  :deep(.screen)
    width 100%
    min-width 0
    max-width none
  :deep(.editable label)
    color PUB_INK
    font-family PUB_FONT_TEXT
    font-size 0.95rem
    font-weight 600
  :deep(input.input)
    box-sizing border-box
    height 44px
    background-color PUB_WHITE
    border 1px solid #8F9BB6
    border-radius 4px
    color PUB_INK
    font-family PUB_FONT_TEXT
    font-size 1rem
    &:focus
      border-color PUB_INK
      outline 3px solid PUB_LEMON
      outline-offset 0
  :deep(p)
    margin 0 0 12px
    font-family PUB_FONT_TEXT
    line-height 1.5
  :deep(.separator label)
    white-space nowrap
    color PUB_MUTED
    font-family PUB_FONT_TEXT
  :deep(.separator span)
    background-color PUB_RULE
  :deep(.reset-password a)
    color PUB_INK
    font-family PUB_FONT_TEXT
    font-weight 600
    text-decoration underline
    text-decoration-color PUB_LEMON_DEEP
    text-decoration-thickness 2px
    text-underline-offset 0.18em
    &:hover
      background-color PUB_LEMON

@media (min-width: 560px)
  .sheet
    padding 30px 30px 32px

@media (min-width: 1000px)
  .auth-grid
    grid-template-columns minmax(0, 1fr) minmax(0, 460px)
    gap 72px
    align-items center
  .auth-grid--solo
    grid-template-columns minmax(0, 1fr)
    padding-left clamp(16px, 4vw, 32px)
  .pitch
    h1
      max-width 18em
  .checks
    display block
    margin 28px 0 0
    padding 0
    list-style none
    li
      position relative
      margin 0 0 10px
      padding 0 0 0 30px
      font-family PUB_FONT_TEXT
      font-size 1.05rem
      &::before
        content ''
        position absolute
        left 6px
        top 0.28em
        width 0.5em
        height 0.95em
        border-right 3px solid PUB_LEAF
        border-bottom 3px solid PUB_LEAF
        transform rotate(40deg)
  .more
    display block
    margin 24px 0 0
    color PUB_MUTED
    font-family PUB_FONT_TEXT
    font-size 0.97rem

@media (min-width: 1240px)
  .auth::before
    left calc(50% - 600px)
</style>
